import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Grid,
    Typography,
    Card,
    CardContent,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Autocomplete,
    TextField,
    Chip,
    Divider,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    useTheme
} from '@mui/material';
import Cookies from 'js-cookie';
import moment from 'moment';
import api from '../../../services/api';
import NavegadorGestao from "../../../components/NavBarGestao";
import MemoryIcon from '@mui/icons-material/Memory';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SecurityIcon from '@mui/icons-material/Security';
import RouterIcon from '@mui/icons-material/Router';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';

export default function TestarModulo() {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const [modulos, setModulos] = useState([]);
    const [tipoModulo, setTipoModulo] = useState('');
    const [moduloSelecionado, setModuloSelecionado] = useState(null);
    const [tarefas, setTarefas] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const tiposSuportados = [
        { id: 'ModuloPGM', label: 'Módulo PGM', icon: <MemoryIcon /> },
        { id: 'ModuloSonoro', label: 'Módulo Sonoro', icon: <VolumeUpIcon /> },
        { id: 'ModuloSentinel', label: 'Módulo Sentinel', icon: <SecurityIcon /> },
        { id: 'ModuloGatewaySentinel', label: 'Gateway Sentinel', icon: <RouterIcon /> }
    ];

    const fetchModulos = async () => {
        try {
            const token = Cookies.get('token');
            const headers = { 'Authorization': 'Bearer ' + token };
            const response = await api.get("v1/protected/modulos/listartodas", { headers });
            setModulos(response.data);
        } catch (error) {
            console.error("Erro ao carregar módulos:", error);
            showSnackbar("Erro ao carregar lista de módulos", "error");
        }
    };

    const fetchTarefas = useCallback(async (id, tipo) => {
        try {
            const token = Cookies.get('token');
            const userId = Cookies.get('id_usuario');
            const headers = { 'Authorization': 'Bearer ' + token };
            
            let endpoint = "";
            if (tipo === "ModuloPGM") {
                endpoint = `v1/protected/modulopgm/tarefas/listartodasparaareatecnica/${userId}/${id}/20`;
            } else if (tipo === "ModuloSonoro") {
                endpoint = `v1/protected/modulosonoro/tarefas/listartodasparaareatecnica/${userId}/${id}/20`;
            }

            if (endpoint) {
                const response = await api.get(endpoint, { headers });
                setTarefas(response.data || []);
            }
        } catch (error) {
            console.error("Erro ao buscar tarefas:", error);
        }
    }, []);

    const fetchDadosModulo = useCallback(async (id, tipo, quiet = false) => {
        if (!quiet) setIsRefreshing(true);
        try {
            const token = Cookies.get('token');
            const headers = { 'Authorization': 'Bearer ' + token };
            
            let endpoint = '';
            if (tipo === 'ModuloPGM') endpoint = `v1/protected/modulopgm/listarporid/${id}`;
            else if (tipo === 'ModuloGatewaySentinel') endpoint = `v1/protected/modulogateway/listarporid/${id}`;
            else if (tipo === 'ModuloSentinel') endpoint = `v1/protected/modulos/sentinel/listarporid/${id}`;
            else if (tipo === 'ModuloSonoro') endpoint = `v1/protected/modulosonoro/listarporid/${id}`;

            if (endpoint) {
                const response = await api.get(endpoint, { headers });
                setModuloSelecionado(response.data);
                // Busca histórico de tarefas
                fetchTarefas(id, tipo);
            }
        } catch (error) {
            console.error("Erro ao atualizar dados do módulo:", error);
            if (!quiet) showSnackbar("Falha ao atualizar dados", "error");
        } finally {
            if (!quiet) setIsRefreshing(false);
        }
    }, [fetchTarefas]);

    useEffect(() => {
        fetchModulos();
    }, []);

    useEffect(() => {
        let interval;
        if (moduloSelecionado) {
            interval = setInterval(() => {
                const id = moduloSelecionado.id_modulo_pgm || moduloSelecionado.id_modulo_sonoro || moduloSelecionado.id;
                fetchDadosModulo(id, tipoModulo, true);
            }, 10000);
        }
        return () => clearInterval(interval);
    }, [moduloSelecionado, tipoModulo, fetchDadosModulo]);

    const modulosFiltrados = useMemo(() => {
        if (!tipoModulo) return [];
        return modulos.filter(m => m.tipo === tipoModulo);
    }, [modulos, tipoModulo]);

    const tipoRequisicaoMap = {
        0: "Acionar Ambos Reles",
        1: "Acionar Rele 1",
        2: "Acionar Rele 2",
        3: "Desligar Ambos Reles",
        4: "Desligar Rele 1",
        5: "Desligar Rele 2",
        6: "Reproduzir Audio Personalizado 1",
        7: "Reproduzir Audio Personalizado 2",
        8: "Reproduzir Audio Personalizado 3",
        9: "Reproduzir Audio Personalizado 4",
        10: "Reproduzir Audio Personalizado 5",
        20: "Desativar Automação Geral",
        21: "Ativar Automação Geral",
        22: "Reiniciar PGM",
        23: "Desativar Automação dos Reles",
        24: "Ativar Automação dos Reles",
        25: "Desativar Automação da Corneta",
        26: "Ativar Automação da Corneta",
        27: "Armar Central em Stay",
        28: "Armar Central em Away",
        29: "Desativar Central",
        50: "Central Armada",
        51: "Central Desarmada",
        52: "Central em Alarme",
        223: "Reiniciar a Central",
        70: "ATUALIZAR PGM",
        700: "PGM REINICIOU",
        900: "ScreenShot"
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSendComando = async (comando) => {
        if (!moduloSelecionado) return;

        try {
            const token = Cookies.get('token');
            const userId = Cookies.get('id_usuario');
            const headers = { 'Authorization': 'Bearer ' + token };
            
            const id = moduloSelecionado.id_modulo_pgm || moduloSelecionado.id_modulo_sonoro || moduloSelecionado.id;
            const url = `/v1/protected/modulopgm/tarefas/criarporareatecnica/${userId}/${id}/${comando}`;
            const response = await api.get(url, { headers });

            if (response.data) {
                showSnackbar(`Comando ${comando} enviado!`);
                setTimeout(() => fetchDadosModulo(id, tipoModulo, true), 1000);
            }
        } catch (error) {
            console.error("Erro ao enviar comando:", error);
            showSnackbar("Falha ao enviar comando", "error");
        }
    };

    const handleDeleteTarefa = async (tarefaId) => {
        if (!window.confirm("Deseja realmente excluir esta tarefa?")) return;

        try {
            const token = Cookies.get('token');
            const headers = { 'Authorization': 'Bearer ' + token };
            
            let endpoint = "";
            if (tipoModulo === "ModuloPGM") endpoint = `v1/protected/modulopgm/tarefas/excluir/${tarefaId}`;
            else if (tipoModulo === "ModuloSonoro") endpoint = `v1/protected/modulosonoro/tarefas/excluir/${tarefaId}`;

            if (endpoint) {
                await api.delete(endpoint, { headers });
                showSnackbar("Tarefa excluída com sucesso");
                const id = moduloSelecionado.id_modulo_pgm || moduloSelecionado.id_modulo_sonoro || moduloSelecionado.id;
                fetchTarefas(id, tipoModulo);
            }
        } catch (error) {
            console.error("Erro ao excluir tarefa:", error);
            showSnackbar("Erro ao excluir tarefa", "error");
        }
    };

    const getStatusOnline = (dataResposta) => {
        if (!dataResposta) return { label: 'OFFLINE', color: 'error' };
        const diffInMinutes = moment().diff(moment(dataResposta), 'minutes');
        return diffInMinutes < 2 ? { label: 'ONLINE', color: 'success' } : { label: 'OFFLINE', color: 'error' };
    };

    const handleRelayToggle = (index, currentStatus) => {
        const isOn = currentStatus === 'on';
        let cmd = index === 1 ? (isOn ? 4 : 1) : (isOn ? 5 : 2);
        handleSendComando(cmd);
    };

    return (
        <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', transition: 'background-color 0.3s ease' }}>
            <NavegadorGestao 
                AreaTecnica={true} 
                Modulos={true} 
                corFundo={isDarkMode ? theme.palette.background.paper : 'white'} 
                travado={true} 
                corTexto={theme.palette.text.primary} 
            />

            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Laboratório de Testes</Typography>
                    {moduloSelecionado && (
                        <Box display="flex" alignItems="center" gap={1}>
                            {isRefreshing && <CircularProgress size={20} />}
                            <Button startIcon={<RefreshIcon />} size="small" onClick={() => {
                                const id = moduloSelecionado.id_modulo_pgm || moduloSelecionado.id_modulo_sonoro || moduloSelecionado.id;
                                fetchDadosModulo(id, tipoModulo);
                            }}>Atualizar</Button>
                        </Box>
                    )}
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: 2, boxShadow: isDarkMode ? 'none' : '0 4px 12px rgba(0,0,0,0.05)', backgroundColor: theme.palette.background.paper }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Seleção</Typography>
                                <FormControl fullWidth sx={{ mt: 2 }}>
                                    <InputLabel>Tipo de Módulo</InputLabel>
                                    <Select value={tipoModulo} label="Tipo de Módulo" onChange={(e) => { setTipoModulo(e.target.value); setModuloSelecionado(null); }}>
                                        {tiposSuportados.map(tipo => (
                                            <MenuItem key={tipo.id} value={tipo.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{tipo.icon} {tipo.label}</Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Autocomplete sx={{ mt: 3 }} options={modulosFiltrados} getOptionLabel={(opt) => `${opt.codigo} - ${opt.modelo || ''}`} value={moduloSelecionado}
                                    onChange={(_, val) => {
                                        if (val) {
                                            const id = val.id_modulo_pgm || val.id_modulo_sonoro || val.id;
                                            fetchDadosModulo(id, tipoModulo);
                                        } else setModuloSelecionado(null);
                                    }}
                                    renderInput={(p) => <TextField {...p} label="Selecionar Módulo" />} disabled={!tipoModulo}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        {moduloSelecionado ? (
                            <Box>
                                <Card sx={{ borderRadius: 2, mb: 3, backgroundColor: theme.palette.background.paper }}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" mb={2}>
                                            <Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="h5" fontWeight="bold">{moduloSelecionado.codigo}</Typography>
                                                    <Tooltip title="Configurações da PGM" placement="top">
                                                        <div
                                                            onClick={() => {
                                                                const url = `/gestao/administracao/equipamentos/editarequipamento/${moduloSelecionado.id_equipamento || moduloSelecionado.id || 0}/1/0/0`;
                                                                window.open(url, '_blank');
                                                            }}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                                                                backgroundColor: isDarkMode ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.08)',
                                                                padding: '2px 8px', borderRadius: '12px',
                                                                border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid rgba(99, 102, 241, 0.25)',
                                                                transition: 'all 0.2s ease-in-out'
                                                            }}
                                                        >
                                                            <SettingsIcon style={{ fontSize: '0.85rem', color: isDarkMode ? '#818cf8' : '#6366f1' }} />
                                                            <Typography style={{ fontSize: '0.65rem', fontWeight: 800, color: isDarkMode ? '#818cf8' : '#6366f1', letterSpacing: '0.5px' }}>
                                                                CONFIG
                                                            </Typography>
                                                        </div>
                                                    </Tooltip>
                                                </Box>
                                                <Typography variant="body2" color="textSecondary">
                                                    {tipoModulo} | {moduloSelecionado.modelo}
                                                    {tipoModulo === 'ModuloPGM' && moduloSelecionado.firmware_version && ` | v${moduloSelecionado.firmware_version}`}
                                                </Typography>
                                            </Box>
                                            <Box textAlign="right">
                                                <Chip label={getStatusOnline(moduloSelecionado.data_hora_ultima_resposta).label} color={getStatusOnline(moduloSelecionado.data_hora_ultima_resposta).color} sx={{ fontWeight: 'bold' }} />
                                                <Typography variant="caption" display="block" mt={1}>Sinc: {moduloSelecionado.data_hora_ultima_resposta ? moment(moduloSelecionado.data_hora_ultima_resposta).format('HH:mm:ss DD/MM/YY') : 'Nunca'}</Typography>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ mb: 2 }} />

                                        {tipoModulo === 'ModuloPGM' && (
                                            <Grid container spacing={2}>
                                                {[1, 2].map(i => {
                                                    const isOn = moduloSelecionado[`status_rele${i}`] === 'on';
                                                    return (
                                                        <Grid item xs={6} key={i}>
                                                            <Box p={2} border={`1px solid ${theme.palette.divider}`} borderRadius={2} textAlign="center" bgcolor={isOn ? (isDarkMode ? '#064e3b' : '#f0fdf4') : theme.palette.background.paper}>
                                                                <Typography variant="subtitle1" fontWeight="bold">Canal {i}</Typography>
                                                                <FormControlLabel control={<Switch checked={isOn} onChange={() => handleRelayToggle(i, moduloSelecionado[`status_rele${i}`])} color="success" />} label={isOn ? "ON" : "OFF"} />
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })}
                                                {Cookies.get("regra") && Cookies.get("regra").includes("ROOT_MASTER") && (
                                                    <Grid item xs={12}>
                                                        <Button variant="contained" color="primary" fullWidth sx={{ py: 1.5, fontWeight: 'bold' }} onClick={() => handleSendComando(70)}>
                                                            ATUALIZAR MÓDULO PGM
                                                        </Button>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        )}

                                        {tipoModulo === 'ModuloSonoro' && (
                                            <Button variant="contained" color="error" fullWidth sx={{ py: 2, fontWeight: 'bold' }} onClick={() => handleSendComando(90)}>ACIONAR ALARME TIPO 90</Button>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card sx={{ borderRadius: 2, backgroundColor: theme.palette.background.paper }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom fontWeight="bold">Histórico de Comandos (Fila)</Typography>
                                        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: theme.palette.action.hover }}>
                                                    <TableRow>
                                                        <TableCell>ID</TableCell>
                                                        <TableCell>Horário</TableCell>
                                                        <TableCell>Comando</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell align="right">Ação</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {tarefas.length > 0 ? tarefas.map((t) => {
                                                        const respondida = tipoModulo === 'ModuloPGM' ? t.respondido_modulo_pgm === 1 : t.respondido_modulo_sonoro === 1;
                                                        return (
                                                            <TableRow key={t.id_tarefa}>
                                                                <TableCell sx={{ fontSize: '0.75rem' }}>{t.id_tarefa}</TableCell>
                                                                <TableCell>{moment(t.data_hora_requisicao).format('HH:mm:ss')}</TableCell>
                                                                <TableCell>
                                                                    <Chip label={`${t.tipo_requisicao} - ${tipoRequisicaoMap[t.tipo_requisicao] || 'Desconhecido'}`} size="small" variant="outlined" />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {respondida ? 
                                                                        <Chip icon={<DoneIcon />} label="Ok" size="small" color="success" variant="outlined" /> : 
                                                                        <Chip icon={<HourglassEmptyIcon />} label="Fila" size="small" color="warning" variant="outlined" />
                                                                    }
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <IconButton size="small" color="error" onClick={() => handleDeleteTarefa(t.id_tarefa)}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    }) : (
                                                        <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: theme.palette.text.secondary }}>Nenhum comando recente</TableCell></TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '350px', border: `2px dashed ${theme.palette.divider}`, borderRadius: 2 }}>
                                <MemoryIcon sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }} />
                                <Typography color="textSecondary">Selecione um módulo para testar.</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}
